import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Clock, Mail, LogIn } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

const DEFAULT_TITLE = 'Underhållsarbete pågår';
const DEFAULT_MESSAGE =
  'Vi uppdaterar plattformen för att ge dig en ännu bättre upplevelse. Vänligen återkom snart!';
const DEFAULT_ETA = 'Några minuter';

const MaintenancePage = ({ title, message, eta }) => {
  const { user, logout, isAdmin } = useAuth();
  const heading = (title || '').trim() || DEFAULT_TITLE;
  const body = (message || '').trim() || DEFAULT_MESSAGE;
  const etaText = (eta || '').trim() || DEFAULT_ETA;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Bygg<span className="text-[#F97316]">Fynd</span>
          </h1>
          <p className="text-white/60 text-sm">Sveriges marknadsplats för byggmaterial</p>
        </div>
        
        {/* Icon */}
        <div className="bg-white/10 backdrop-blur-sm rounded-full p-6 w-24 h-24 mx-auto mb-8 flex items-center justify-center">
          <Wrench className="h-12 w-12 text-[#F97316] animate-pulse" />
        </div>
        
        {/* Message */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">
            {heading}
          </h2>
          <p className="text-white/70 mb-6 whitespace-pre-wrap">
            {body}
          </p>
          
          {/* Status */}
          <div className="flex items-center justify-center gap-2 text-[#F97316] mb-6">
            <Clock className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium">Beräknad tid: {etaText}</span>
          </div>
          
          {/* Contact */}
          <div className="pt-6 border-t border-white/10 space-y-4">
            <div>
              <Link
                to="/logga-in"
                className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 px-4 transition-colors"
              >
                <LogIn className="h-5 w-5" />
                Administratör – logga in här
              </Link>
              <p className="text-white/45 text-xs mt-2 text-center">
                Öppnar inloggning. Under underhåll kan endast admin-konton logga in.
              </p>
            </div>
            {user && !isAdmin ? (
              <div className="rounded-lg bg-white/10 border border-white/15 p-3 text-left">
                <p className="text-white/80 text-sm mb-2">
                  Du är inloggad som <span className="font-medium text-white">{user.email}</span> (ej admin). Logga ut för att kunna logga in som administratör.
                </p>
                <Button type="button" variant="secondary" size="sm" className="w-full" onClick={() => logout()}>
                  Logga ut
                </Button>
              </div>
            ) : null}
            <div>
              <p className="text-white/50 text-sm mb-2">Behöver du hjälp?</p>
              <a
                href="mailto:support@byggfynd.se"
                className="inline-flex items-center gap-2 text-[#F97316] hover:text-[#FB923C] transition-colors"
              >
                <Mail className="h-4 w-4" />
                support@byggfynd.se
              </a>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <p className="mt-8 text-white/40 text-xs">
          © {new Date().getFullYear()} ByggFynd.se - Alla rättigheter förbehållna
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
